// Recorded takes live in this browser's IndexedDB: a song link carries only
// where each clip sits, so a reload on this device brings the audio back, and
// a link opened elsewhere plays without it.
import {recordedTakes, Song} from "../synth/synth.js";

const DATABASE = "bop-recordings";
const STORE = "takes";

function database(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(DATABASE, 1);
		request.onupgradeneeded = () => request.result.createObjectStore(STORE);
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

/** A short random id that is safe in the song's URL encoding. */
export function newTakeId(): string {
	const alphabet = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ-_";
	const bytes = crypto.getRandomValues(new Uint8Array(12));
	return Array.from(bytes, b => alphabet[b & 63]).join("");
}

export async function saveTake(id: string, rate: number, data: Float32Array): Promise<void> {
	recordedTakes.set(id, {rate, data});
	const db = await database();
	await new Promise<void>((resolve, reject) => {
		const transaction = db.transaction(STORE, "readwrite");
		transaction.objectStore(STORE).put({rate, data}, id);
		transaction.oncomplete = () => resolve();
		transaction.onerror = () => reject(transaction.error);
	});
	db.close();
}

/** Takes the song refers to, in the order its clips use them. */
export function referencedTakes(song: Song): string[] {
	return [...new Set(song.channels.flatMap(channel => channel.clips.map(clip => clip.take)))];
}

/** Ids the song uses that are not on this device. */
export function missingTakes(song: Song): string[] {
	return referencedTakes(song).filter(id => !recordedTakes.has(id));
}

/** Loads any of the song's takes saved on this device; resolves to the ids
 * that were loaded (nothing to do resolves to an empty list). */
export async function loadTakes(song: Song): Promise<string[]> {
	const wanted = missingTakes(song);
	if (wanted.length == 0 || typeof indexedDB == "undefined") return [];
	const db = await database();
	const loaded: string[] = [];
	await Promise.all(wanted.map(id => new Promise<void>(resolve => {
		const request = db.transaction(STORE).objectStore(STORE).get(id);
		request.onsuccess = () => {
			const take = request.result;
			if (take && take.data instanceof Float32Array && take.rate > 0) {
				recordedTakes.set(id, {rate: take.rate, data: take.data});
				loaded.push(id);
			}
			resolve();
		};
		request.onerror = () => resolve();
	})));
	db.close();
	return loaded;
}
